import type { GLTFLoader as BaseLoader, GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { tallyDecoded } from './gpuTally';

// THE ONE GLTF LOADER OF THE APP, handed out by loadDeps as `GLTFLoader` so every `new
// GLTFLoader()` already written gets it without being edited.
//
// IT READS A PACKED MESH (backend/glbpack.py, asked for by meshSrc): Meshopt-compressed buffers,
// decoded by three's own MeshoptDecoder in milliseconds, and KTX2 textures, transcoded for this
// GPU by `ktx2` and kept compressed on it. An unpacked file loads as it always did.
//
// AND IT HANDS BACK FLOATS. A packed mesh's positions are 16-bit integers, its normals 8-bit, with
// the scale that makes them millimetres on a node above them. Everything that reads a point
// through `matrixWorld` gets the right answer from that as it is — the hole and fit rays, the
// measures, the drawn mesh. What does not is code that MOVES the vertices in place: the clash
// measure copies each geometry and applies a matrix to it (assemblyClash), and an integer array
// rounds every moved coordinate. Measured on part 365, that copy's box came out 159 mm wrong.
// So every attribute is a Float32Array again before anything sees the mesh: the download keeps
// its saving, the memory goes back to what it was, and no reader has a second case to handle.
export function packedLoader(
  THREE: typeof import('three'),
  Base: typeof BaseLoader,
  decoder: Parameters<BaseLoader['setMeshoptDecoder']>[0],
  ktx2: Parameters<BaseLoader['setKTX2Loader']>[0],
): typeof BaseLoader {
  const floats = (scene: import('three').Object3D) => {
    const seen = new Set<import('three').BufferGeometry>();
    scene.traverse((o) => {
      const geometry = (o as import('three').Mesh).geometry;
      // A packed file stores a mesh placed many times once, so its geometry is shared.
      if (!geometry || seen.has(geometry)) return;
      seen.add(geometry);
      for (const [name, attr] of Object.entries(geometry.attributes)) {
        const plain = attr as import('three').BufferAttribute;
        if (!('isInterleavedBufferAttribute' in attr) && plain.array instanceof Float32Array) continue;
        const out = new Float32Array(attr.count * attr.itemSize);
        for (let i = 0; i < attr.count; i += 1) {
          for (let k = 0; k < attr.itemSize; k += 1) out[i * attr.itemSize + k] = attr.getComponent(i, k);
        }
        geometry.setAttribute(name, new THREE.BufferAttribute(out, attr.itemSize));
      }
    });
  };

  return class extends Base {
    constructor(manager?: import('three').LoadingManager) {
      super(manager);
      this.setMeshoptDecoder(decoder);
      this.setKTX2Loader(ktx2);
    }

    // load and loadAsync both come through here.
    parse(
      data: string | ArrayBuffer,
      path: string,
      onLoad: (gltf: GLTF) => void,
      onError?: (event: ErrorEvent) => void,
    ): void {
      const shapes = shapeKeys(data);
      super.parse(data, path, (gltf) => {
        if (shapes) shareShapes(gltf, shapes);
        floats(gltf.scene);
        tallyDecoded(gltf.scene);
        onLoad(gltf);
      }, onError);
    }
  };
}

// ONE SHAPE IN MEMORY FOR EVERY COLOURWAY OF IT (Louis 2026-10-26).
//
// A colourway's file holds its paint and points at its shape in a file of its own, named by the
// hash of its bytes (backend glbpack.split), so four colourways of a chair download one shape.
// But each is a different file, read on its own, and was decoded into a geometry of its own: the
// published tutorial's four colourway tiles held the chair four times over, about 46 MB each, on
// a page an iPhone was already losing for want of memory.
//
// So a primitive whose every accessor reads the shape file is keyed by WHERE in that file it
// reads, and the second colourway to decode it is handed the first one's geometry. Shared the way
// a file placed twice already shares one (see loadCopy): the materials stay each file's own, and
// nothing in the app moves the vertices of a loaded geometry in place. The decoding itself still
// runs per file; what is dropped is everything it made, before anything has drawn it.
const shared = new Map<string, import('three').BufferGeometry>();

type GltfJson = {
  buffers?: { uri?: string }[];
  bufferViews?: {
    buffer?: number; byteOffset?: number; byteLength?: number; byteStride?: number;
    extensions?: { EXT_meshopt_compression?: Record<string, unknown> & { buffer?: number } };
  }[];
  accessors?: { bufferView?: number; byteOffset?: number; count?: number; type?: string; componentType?: number; normalized?: boolean }[];
  meshes?: { primitives?: { attributes?: Record<string, number>; indices?: number; mode?: number; targets?: unknown[] }[] }[];
};

// The key of every primitive of a GLB that reads its geometry from a shape file, by
// "mesh/primitive"; null where the file is not a GLB or points at no shape.
function shapeKeys(data: string | ArrayBuffer): Map<string, string> | null {
  if (typeof data === 'string' || data.byteLength < 20) return null;
  const head = new DataView(data);
  if (head.getUint32(0, true) !== 0x46546c67 || head.getUint32(16, true) !== 0x4e4f534a) return null;
  let doc: GltfJson;
  try {
    doc = JSON.parse(new TextDecoder().decode(new Uint8Array(data, 20, head.getUint32(12, true))));
  } catch {
    return null;
  }
  const shapeOf = new Map<number, string>();
  (doc.buffers ?? []).forEach((b, i) => {
    const sha = /shape\/([0-9a-f]{64})\.bin$/.exec(b.uri ?? '')?.[1];
    if (sha) shapeOf.set(i, sha);
  });
  if (!shapeOf.size) return null;
  // Where one accessor's bytes are, or null when they are not all in a shape file.
  const where = (index: number | undefined) => {
    const acc = index === undefined ? undefined : doc.accessors?.[index];
    const view = acc?.bufferView === undefined ? undefined : doc.bufferViews?.[acc.bufferView];
    if (!acc || !view) return null;
    const meshopt = view.extensions?.EXT_meshopt_compression;
    const sha = shapeOf.get(meshopt ? meshopt.buffer ?? 0 : view.buffer ?? 0);
    if (!sha) return null;
    const { bufferView: _, ...rest } = acc;
    return { sha, view: { ...view, buffer: undefined, extensions: undefined }, meshopt: meshopt && { ...meshopt, buffer: undefined }, acc: rest };
  };
  const keys = new Map<string, string>();
  (doc.meshes ?? []).forEach((mesh, m) => (mesh.primitives ?? []).forEach((prim, p) => {
    if (prim.targets?.length) return;
    const parts = [
      ...Object.entries(prim.attributes ?? {}).sort(([a], [b]) => a.localeCompare(b)).map(([name, i]) => [name, where(i)]),
      ['indices', prim.indices === undefined ? 'none' : where(prim.indices)],
    ];
    if (parts.some(([, w]) => w === null)) return;
    keys.set(`${m}/${p}`, JSON.stringify([prim.mode ?? 4, parts]));
  }));
  return keys.size ? keys : null;
}

function shareShapes(gltf: GLTF, keys: Map<string, string>) {
  gltf.scene.traverse((o) => {
    const mesh = o as import('three').Mesh;
    if (!mesh.isMesh) return;
    const at = gltf.parser.associations.get(mesh) as { meshes?: number; primitives?: number } | undefined;
    const key = at?.meshes === undefined ? undefined : keys.get(`${at.meshes}/${at.primitives ?? 0}`);
    if (!key) return;
    const first = shared.get(key);
    if (first) mesh.geometry = first;
    else shared.set(key, mesh.geometry);
  });
}
